/**
 * Org-Owner or Super-Admin Guard (Sprint 4a G4-2 finish).
 *
 * Wraps Sentinel moderator routes (/sentinel/*) so the SPA shell never
 * renders the moderator UI for users without an owner/admin role. The
 * backend (`require_super_admin_or_org_owner` in web/api/sentinel.py)
 * enforces the same rule — this guard is defence-in-depth so the FE
 * doesn't fire moderator API calls that will 403.
 *
 * Behaviour:
 *  - super_admin (CurAItion staff) → renders children
 *  - org-owner / org-admin → renders children
 *  - portal member / viewer → their configured portal home
 *  - unaffiliated (no super_admin, no org role) → /dashboard
 *
 * Sprint 4a v0.2 spec §"Decisions baked in" #4 — "Webhook RBAC v1:
 * Org-Owner/Admin only". At Gymshark this means Callum-only initially;
 * regular brand-team members hit the redirect.
 */

import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/auth/useAuth';
import { usePortalHome } from '@/hooks/portal/usePortalHome';

const _OWNER_ROLES: ReadonlySet<string> = new Set(['owner', 'admin']);

interface OrgOwnerOrSuperAdminGuardProps {
  children: ReactNode;
}

export function OrgOwnerOrSuperAdminGuard({ children }: OrgOwnerOrSuperAdminGuardProps) {
  const { isSuperAdmin, isPortalUser, organizationRole } = useAuth();
  const home = usePortalHome();

  if (isSuperAdmin) {
    return <>{children}</>;
  }

  const hasOwnerRole = organizationRole !== undefined && _OWNER_ROLES.has(organizationRole);
  if (hasOwnerRole) {
    return <>{children}</>;
  }

  // Portal users (org members / viewers without owner role) go to their
  // portal home rather than the admin dashboard they can't see anyway.
  if (isPortalUser) {
    return <Navigate to={home} replace />;
  }

  // Unaffiliated admin-shell users get bumped to the admin home.
  return <Navigate to="/dashboard" replace />;
}
